/**
 * The person's appearance choice, applied before the first paint. The choice is kept in this browser as well as on the
 * server, so a reload shows it at once instead of after the first request. Until dark mode ships only "light" and
 * "system" take effect; a saved "dark" follows the computer like "system" does.
 */
export type ThemeChoice = "system" | "light" | "dark";

const KEY = "adea.theme";

export function savedTheme(): ThemeChoice {
  try {
    const value = window.localStorage.getItem(KEY);
    if (value === "light" || value === "dark" || value === "system") return value;
  } catch {
    // Storage can be blocked; the computer's setting then decides.
  }
  return "system";
}

/** Light or dark now, from the choice and the computer's setting. */
export function applyTheme(choice: ThemeChoice): void {
  const system = window.matchMedia("(prefers-color-scheme: dark)").matches;
  document.documentElement.classList.toggle("dark", choice === "light" ? false : system);
}

/** Remember the choice here and show it. */
export function setTheme(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(KEY, choice);
  } catch {
    // Not remembered on this browser; the server still has it.
  }
  applyTheme(choice);
}

/** Boot: apply the saved choice and follow the computer's setting while the choice is "system". */
export function startTheme(): void {
  applyTheme(savedTheme());
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => applyTheme(savedTheme()));
}
